import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop, Emit } from 'vue-property-decorator';
import * as QuillCursors from 'quill-cursors';
import { setQuillInlineStyle } from '@/components/common/ed-collaborative-text-editor/formats/attributors';
import Quill from 'quill';
import mathquill4quill from 'mathquill4quill';
import 'mathquill4quill/mathquill4quill.css';
import 'mathquill/build/mathquill';
import 'mathquill/build/mathquill.css';
import katex from 'katex';
import 'katex/dist/katex.css';
import { MATHQUILL_FORMULA_OPERATORS } from '@/values/quill.values';
import {deltaHasText, enableFormattingEmbeds} from './collaborative-text.utils';
declare const window: any;

@Component({
  name: 'ed-collaborative-text',
  components: {
    EdCollaborativeTextEditor: () => import('@/components/common/ed-collaborative-text-editor/ed-collaborative-text-editor.vue'),
    EdCollaborativeTextToolbar: () => import('@/components/common/ed-collaborative-text-editor/ed-collaborative-text-toolbar.vue'),
  }
})
export default class EdCollaborativeText extends Vue {
  @Prop({default:false}) readonly readOnly: boolean;
  @Prop({default:true}) readonly mediaOptions: boolean;
  @Prop({default:false}) readonly enableFormulas: boolean;
  @Prop({default:false}) readonly undoRedoOptions: boolean;
  @Prop({default:true}) readonly showToolbar: boolean;
  @Prop() readonly minHeight: string;
  @Prop({default:false}) readonly validateEditorEmpty: boolean;

  public quillEditor: Quill = null;
  private toolbarReady: boolean = false;
  private editorReady: boolean = false;

  public toolbarId: string = 'toolbar-' + Math.random().toString(36).slice(-6);
  public editorId: string = 'editor-' + Math.random().toString(36).slice(-6);
  private mathquill4quillCacheKey: string = `__mathquill4quill_cache_${this.editorId}`;

  get hasTextEditorText() {
    return deltaHasText(this.quillEditor?.getContents());
  }

  public loadToolbarFinished() {    
    this.toolbarReady = true;
    this.initQuill();
  }

  public loadEditorFinished() {
    this.editorReady = true;
    this.initQuill();
  }

  private checkMatcherTagHtmlP(node, delta) {
    if(node.hasAttribute('style')) {
      let styleValue = node.getAttributeNode('style').value;
      if(styleValue && styleValue.includes('text-align:left;padding-left:')) {
        delta.map(function(op) {
          if (!op.attributes) op.attributes = {}
          const resultRegex = styleValue.match(new RegExp('left:' + '(.*)' + 'px'));
          let result : number = +resultRegex[1];
          op.attributes.indent = result / 20;
        });
      }
    }
    return delta;
  }

  private validateEditorIsEmpty(delta: any) {
    this.textEditorEmpty(!this.hasTextEditorText);
  }

  private initQuill() {
    window.katex = katex;
    if(this.toolbarReady && this.editorReady) {      
      setQuillInlineStyle();
      
      Quill.register({ 'modules/cursors': QuillCursors.default }, true);
      Quill.register({'modules/formula': mathquill4quill}, true);
      this.quillEditor = new Quill(document.getElementById(this.editorId), {
        modules: {
          toolbar: `#${this.toolbarId}`,
          preserveFormatOnEnter: true,
          cursors: {
            hideDelayMs: 3000,
            hideSpeedMs: 3000,
          },
          formula: true,
          history: {
            userOnly: true
          }
        },
        theme: 'snow',
        readOnly: this.readOnly
      });
      if(this.validateEditorEmpty) {
        this.quillEditor.on('text-change', this.validateEditorIsEmpty);
      }
      this.initFormulaEditor();
      this.initQuillFinished();
    }
  }

  private initFormulaEditor() {
      const enableMathQuillFormulaAuthoring = mathquill4quill({ Quill });
      enableMathQuillFormulaAuthoring(this.quillEditor, {
        operators: MATHQUILL_FORMULA_OPERATORS,
        displayHistory: true,
        historyCacheKey: '__my_app_math_history_cachekey__',
        historySize: 20,
        cacheKey: this.mathquill4quillCacheKey
      });
      enableFormattingEmbeds(this.quillEditor);  
    }

  getHTML() {
    return this.quillEditor.root.innerHTML;
  }

  @Emit('initQuillFinished')
  private initQuillFinished() {
    return true;
  }

  @Emit('textEditorEmpty')
  private textEditorEmpty(data) {
    return data;
  }

  beforeDestroy() {
    // Delete the cache item of the mathquill input
    try {
      localStorage.removeItem(this.mathquill4quillCacheKey);
    } catch (e) {
      // eslint-disable-line no-empty
    }
  }

}
